export type RoleName =
  | 'superadmin'
  | 'admin'
  | 'analyst'
  | 'readonly'
  | 'financeviewer';

export const ROLE_OPTIONS = [
  { value: 'admin' as const, label: 'Admin' },
  { value: 'analyst' as const, label: 'Analyst' },
  { value: 'readonly' as const, label: 'Read Only' },
] as const;

// superadmin is intentionally NOT part of ROLE_OPTIONS: that array feeds several
// ungated role pickers (invite-user, SSO default-role configs, add-customer wizard).
// It is surfaced only through getFilteredRoleOptions when the caller is entitled.
export const SUPERADMIN_OPTION = {
  value: 'superadmin' as const,
  label: 'Super Admin',
} as const;

// financeviewer is Praetorian-only, like superadmin — injected via getFilteredRoleOptions.
export const FINANCEVIEWER_OPTION = {
  value: 'financeviewer' as const,
  label: 'Finance Viewer',
} as const;

export function formatRoleName(role?: string): string {
  switch (role) {
    case 'superadmin':
      return 'Super Admin';
    case 'admin':
      return 'Admin';
    case 'analyst':
      return 'Analyst';
    case 'readonly':
      return 'Read Only';
    case 'financeviewer':
      return 'Finance Viewer';
    default:
      return 'Read Only';
  }
}

const ROLE_HIERARCHY: RoleName[] = [
  'superadmin',
  'admin',
  'analyst',
  'readonly',
  'financeviewer',
];

export function getFilteredRoleOptions(
  userRole?: string,
  canAssignSuperAdmin = false,
  targetEmail?: string,
  isPraetorian = false
): { value: RoleName; label: string }[] {
  const userIndex = ROLE_HIERARCHY.indexOf(userRole as RoleName);
  if (userIndex === -1) {
    // Unknown or missing role — safest default is readonly only
    return ROLE_OPTIONS.filter(opt => opt.value === 'readonly');
  }
  const allowedRoles = ROLE_HIERARCHY.slice(userIndex);
  const base = ROLE_OPTIONS.filter(opt => allowedRoles.includes(opt.value));
  // superadmin is not in ROLE_OPTIONS; inject it only when the caller holds the
  // super_admin entitlement (gated on the entitlement ALONE, not on rank: bootstrap
  // allow-list holders have role='admin' and could otherwise never assign the first
  // superadmin) AND the target member is a @praetorian.com account. The backend
  // (validateRoleGrant) only allows granting superadmin to @praetorian.com recipients,
  // so surfacing the option for a customer-user row would guarantee a 400 on submit.
  const targetIsPraetorian =
    targetEmail?.toLowerCase().endsWith('@praetorian.com') ?? false;
  const options: { value: RoleName; label: string }[] =
    canAssignSuperAdmin && targetIsPraetorian
      ? [SUPERADMIN_OPTION, ...base]
      : [...base];
  if (isPraetorian) {
    options.push(FINANCEVIEWER_OPTION);
  }
  return options;
}

export function getRoleChipStyle(
  role?: string
): 'primary' | 'secondary' | 'default' {
  switch (role) {
    case 'superadmin':
      return 'primary';
    case 'admin':
      return 'primary';
    case 'analyst':
      return 'secondary';
    case 'financeviewer':
      return 'secondary';
    default:
      return 'default';
  }
}
